import {
  type AgskClassList,
  CONSUME_REASONS,
  type ConsumeReason,
  CREATABLE_DOC_KINDS,
  type Dict1cRow,
  DOC_KINDS,
  type DocKind,
  type DocumentDetail,
  type DocumentInput,
  ITEM_KINDS,
  ITEM_PROPS,
  type Item,
  type ItemInput,
  type ItemProp,
  type ReservationInput,
  type SimilarItem,
  seesMoney,
  WAREHOUSE_KINDS,
  type Warehouse,
  type WarehouseInput,
  type WarehouseKind,
  type WorkLimit,
} from '@epc/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { Plus, Trash2 } from 'lucide-react';
import { useMemo, useState } from 'react';
import { api } from '../../lib/api';
import { todayLocal } from '../../lib/format';
import { counterpartiesQuery, projectsQuery } from '../../lib/projects';
import { useMe } from '../../lib/session';
import { Button, IconButton } from '../../ui/button';
import { Dialog } from '../../ui/dialog';
import { Badge, ErrorBox, toast } from '../../ui/feedback';
import { Checkbox, Field, Input, Select, Textarea } from '../../ui/form';
import {
  formKey,
  holdingsQuery,
  itemsQuery,
  parseNum,
  peopleQuery,
  qty,
  stockQuery,
  warehousesQuery,
} from './queries';

/* ───────────────────────── Склад ───────────────────────── */

export function WarehouseForm({
  initial,
  onSaved,
  onCancel,
}: {
  initial?: Warehouse;
  onSaved: (w: Warehouse) => void;
  onCancel?: () => void;
}) {
  const qc = useQueryClient();
  const projects = useQuery(projectsQuery('active'));
  const people = useQuery(peopleQuery);
  const contractors = useQuery(counterpartiesQuery('contractor'));
  const customers = useQuery(counterpartiesQuery('customer'));
  const [f, setF] = useState({
    kind: (initial?.kind ?? 'site') as WarehouseKind,
    name: initial?.name ?? '',
    projectId: initial?.projectId ?? '',
    objectId: initial?.objectId ?? '',
    counterpartyId: initial?.counterpartyId ?? '',
    address: initial?.address ?? '',
    molUserId: initial?.molUserId ?? '',
    storekeeperIds: initial?.storekeeperIds ?? ([] as string[]),
    code1c: initial?.code1c ?? '',
    note: initial?.note ?? '',
    isActive: initial?.isActive ?? true,
  });
  const objects = projects.data?.find((p) => p.id === f.projectId)?.objects ?? [];
  const m = useMutation({
    mutationFn: () => {
      const body: WarehouseInput = {
        kind: f.kind,
        name: f.name.trim(),
        projectId: f.projectId || null,
        objectId: f.objectId || null,
        counterpartyId: f.counterpartyId || null,
        address: f.address.trim() || null,
        molUserId: f.molUserId || null,
        storekeeperIds: f.storekeeperIds,
        code1c: f.code1c.trim() || null,
        note: f.note.trim() || null,
        isActive: f.isActive,
      };
      return initial
        ? api<Warehouse>(`/warehouse/warehouses/${initial.id}`, { method: 'PATCH', body })
        : api<Warehouse>('/warehouse/warehouses', { body });
    },
    onSuccess: (w) => {
      qc.invalidateQueries({ queryKey: ['wh'] });
      toast(initial ? 'Склад сохранён' : 'Склад заведён');
      onSaved(w);
    },
  });
  return (
    <div className="flex flex-col gap-4">
      <div className="grid gap-4 sm:grid-cols-2">
        <Field
          label="Вид склада"
          hint={
            f.kind === 'customer'
              ? 'Давальческие материалы заказчика: учитываются отдельно от своих, со своими складами и отчётом заказчику (СК-25). Вид потом не меняется'
              : undefined
          }
        >
          <Select
            value={f.kind}
            disabled={Boolean(initial) && (initial?.kind === 'customer' || f.kind === 'customer')}
            onChange={(e) => setF({ ...f, kind: e.target.value as WarehouseKind, counterpartyId: '' })}
          >
            {(Object.keys(WAREHOUSE_KINDS) as WarehouseKind[])
              .filter((k) => k !== 'transit' && (k !== 'customer' || !initial || initial.kind === 'customer'))
              .map((k) => (
                <option key={k} value={k}>
                  {WAREHOUSE_KINDS[k]}
                </option>
              ))}
          </Select>
        </Field>
        <Field label="Название">
          <Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} />
        </Field>
        <Field label="Проект" hint="Центральный склад — без проекта">
          <Select
            value={f.projectId}
            onChange={(e) => setF({ ...f, projectId: e.target.value, objectId: '' })}
          >
            <option value="">Без проекта</option>
            {(projects.data ?? []).map((p) => (
              <option key={p.id} value={p.id}>
                {p.code} · {p.name}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Объект">
          <Select
            value={f.objectId}
            disabled={!objects.length}
            onChange={(e) => setF({ ...f, objectId: e.target.value })}
          >
            <option value="">—</option>
            {objects.map((o) => (
              <option key={o.id} value={o.id}>
                {o.name}
              </option>
            ))}
          </Select>
        </Field>
        {f.kind === 'customer' ? (
          <Field
            label="Заказчик"
            className="sm:col-span-2"
            hint="Чьи материалы хранятся. Нужен и проект: материалы заказчика числятся по его договору и проекту"
          >
            <Select
              value={f.counterpartyId}
              disabled={Boolean(initial)}
              onChange={(e) => setF({ ...f, counterpartyId: e.target.value })}
            >
              <option value="">Выберите заказчика</option>
              {(customers.data ?? []).map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </Select>
          </Field>
        ) : null}
        {f.kind === 'contractor' ? (
          <Field label="Подрядчик" className="sm:col-span-2">
            <Select value={f.counterpartyId} onChange={(e) => setF({ ...f, counterpartyId: e.target.value })}>
              <option value="">Выберите подрядчика</option>
              {(contractors.data ?? []).map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </Select>
          </Field>
        ) : null}
        <Field label="Адрес" className="sm:col-span-2">
          <Input value={f.address} onChange={(e) => setF({ ...f, address: e.target.value })} />
        </Field>
        <Field label="Материально ответственное лицо">
          <Select value={f.molUserId} onChange={(e) => setF({ ...f, molUserId: e.target.value })}>
            <option value="">—</option>
            {(people.data ?? []).map((p) => (
              <option key={p.id} value={p.id}>
                {p.fullName}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Код склада в 1С">
          <Input value={f.code1c} onChange={(e) => setF({ ...f, code1c: e.target.value })} />
        </Field>
      </div>
      <fieldset className="flex flex-col gap-2">
        <legend className="mb-1.5 text-[13px] font-medium">Кладовщики</legend>
        <div className="grid gap-x-5 gap-y-1.5 sm:grid-cols-2">
          {(people.data ?? []).map((p) => (
            <Checkbox
              key={p.id}
              label={p.fullName}
              description={p.position ?? undefined}
              checked={f.storekeeperIds.includes(p.id)}
              onChange={(e) =>
                setF({
                  ...f,
                  storekeeperIds: e.target.checked
                    ? [...f.storekeeperIds, p.id]
                    : f.storekeeperIds.filter((x) => x !== p.id),
                })
              }
            />
          ))}
        </div>
      </fieldset>
      <Field label="Примечание">
        <Textarea rows={2} value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} />
      </Field>
      {initial ? (
        <Checkbox
          label="Склад работает"
          description="Закрытый склад не принимает документы"
          checked={f.isActive}
          onChange={(e) => setF({ ...f, isActive: e.target.checked })}
        />
      ) : null}
      <ErrorBox error={m.error} />
      <div className="flex gap-2">
        <Button
          variant="primary"
          loading={m.isPending}
          disabled={f.name.trim().length < 2 || (f.kind === 'customer' && !(f.counterpartyId && f.projectId))}
          onClick={() => m.mutate()}
        >
          {initial ? 'Сохранить склад' : 'Завести склад'}
        </Button>
        {onCancel ? (
          <Button variant="ghost" onClick={onCancel}>
            Отмена
          </Button>
        ) : null}
      </div>
    </div>
  );
}

/** Класс АГСК-3 из нормативной базы (СК-02): поиск по коду и названию, ручной ввод остаётся. */
function AgskClassField({
  value,
  itemKind,
  onChange,
  onPickUnit,
}: {
  value: string;
  itemKind: 'material' | 'tool';
  onChange: (code: string) => void;
  onPickUnit?: (unit: string) => void;
}) {
  const q = value.trim();
  const classifier = useQuery({
    queryKey: ['norms', 'classifier', itemKind, q],
    queryFn: () =>
      api<AgskClassList>('/norms/classifier', {
        query: {
          q: q || undefined,
          kind: itemKind === 'material' ? 'material' : undefined,
          limit: 12,
        },
      }),
    enabled: q.length >= 2,
    retry: false,
  });
  const rows = classifier.data?.rows ?? [];
  return (
    <Field
      label="Класс АГСК-3"
      hint="из классификатора нормативной базы; можно ввести код вручную"
      className="sm:col-span-1"
    >
      <Input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder="216-103-0101 или название"
        className="!h-11"
      />
      {classifier.isError ? (
        <p className="mt-1 text-[12px] text-ink-3">Справочник недоступен — введите код вручную.</p>
      ) : null}
      {rows.length ? (
        <div className="mt-2 flex max-h-48 flex-col gap-1 overflow-y-auto">
          {rows.map((r) => (
            <button
              key={r.code}
              type="button"
              onClick={() => {
                onChange(r.code);
                if (r.unit) onPickUnit?.(r.unit);
              }}
              className="inline-flex min-h-11 items-center rounded-md border border-line bg-surface px-3 text-left text-[13px] hover:border-line-strong"
              title={`${r.name} · ${r.unit}`}
            >
              <span className="num shrink-0 font-medium">{r.code}</span>
              <span className="ml-2 min-w-0 truncate text-ink-2">{r.name}</span>
              <span className="ml-auto shrink-0 pl-2 text-ink-3">{r.unit}</span>
            </button>
          ))}
        </div>
      ) : q.length >= 2 && classifier.isFetched && !classifier.isError ? (
        <p className="mt-1 text-[12px] text-ink-3">В загруженной базе нет совпадений — оставьте свой код.</p>
      ) : null}
    </Field>
  );
}

/* ───────────────────────── Позиция каталога ───────────────────────── */

export function ItemForm({
  initial,
  onSaved,
  onCancel,
}: {
  initial?: Item;
  onSaved: (i: Item) => void;
  onCancel?: () => void;
}) {
  const qc = useQueryClient();
  const [f, setF] = useState({
    name: initial?.name ?? '',
    kind: (initial?.kind ?? 'material') as 'material' | 'tool',
    agskClass: initial?.agskClass ?? '',
    props: { ...(initial?.props ?? {}) } as Record<string, string>,
    unit: initial?.unit ?? 'шт',
    convUnit: initial?.convUnit ?? '',
    convFactor: initial?.convFactor ? String(initial.convFactor) : '',
    certRequired: initial?.certRequired ?? false,
    passportRequired: initial?.passportRequired ?? false,
    shelfLifeDays: initial?.shelfLifeDays ? String(initial.shelfLifeDays) : '',
    minStock: initial?.minStock !== null && initial?.minStock !== undefined ? String(initial.minStock) : '',
    targetStock:
      initial?.targetStock !== null && initial?.targetStock !== undefined ? String(initial.targetStock) : '',
    code1c: initial?.code1c ?? '',
    note: initial?.note ?? '',
    isActive: initial?.isActive ?? true,
  });
  // Подсказка кода номенклатуры из загруженного справочника 1С (СК-35) — по названию позиции
  const hints = useQuery({
    queryKey: ['wh', '1c', 'dict', 'nomenclature', f.name],
    queryFn: () =>
      api<Dict1cRow[]>('/warehouse/1c/dict/search', { query: { kind: 'nomenclature', q: f.name.trim() } }),
    enabled: f.name.trim().length >= 4 && !f.code1c.trim(),
    retry: false,
  });
  const propsClean = useMemo(
    () => Object.fromEntries(Object.entries(f.props).filter(([, v]) => v.trim())),
    [f.props],
  );
  const similar = useQuery({
    queryKey: ['wh', 'items', 'similar', f.name, f.agskClass, propsClean, initial?.id],
    queryFn: () =>
      api<SimilarItem[]>('/warehouse/items/similar', {
        query: {
          name: f.name,
          agskClass: f.agskClass || undefined,
          props: JSON.stringify(propsClean),
          exclude: initial?.id,
        },
      }),
    enabled: f.name.trim().length >= 4,
  });
  const m = useMutation({
    mutationFn: () => {
      const body: ItemInput = {
        name: f.name.trim(),
        kind: f.kind,
        agskClass: f.agskClass.trim() || null,
        props: propsClean,
        unit: f.unit.trim(),
        convUnit: f.convUnit.trim() || null,
        convFactor: f.convFactor.trim() ? parseNum(f.convFactor) : null,
        certRequired: f.certRequired,
        passportRequired: f.passportRequired,
        shelfLifeDays: f.shelfLifeDays.trim() ? Number(f.shelfLifeDays) : null,
        minStock: f.minStock.trim() ? parseNum(f.minStock) : null,
        targetStock: f.targetStock.trim() ? parseNum(f.targetStock) : null,
        code1c: f.code1c.trim() || null,
        note: f.note.trim() || null,
        isActive: f.isActive,
      };
      return initial
        ? api<Item>(`/warehouse/items/${initial.id}`, { method: 'PATCH', body })
        : api<Item>('/warehouse/items', { body });
    },
    onSuccess: (i) => {
      qc.invalidateQueries({ queryKey: ['wh'] });
      toast(initial ? 'Позиция сохранена' : `Позиция ${i.code} заведена`);
      onSaved(i);
    },
  });
  const dups = similar.data ?? [];
  return (
    <div className="flex flex-col gap-4">
      <div className="grid gap-4 sm:grid-cols-3">
        <Field label="Наименование" className="sm:col-span-2">
          <Input
            value={f.name}
            onChange={(e) => setF({ ...f, name: e.target.value })}
            placeholder="Труба ПЭ100 SDR17 Ø225"
          />
        </Field>
        <Field label="Вид">
          <Select
            value={f.kind}
            onChange={(e) => setF({ ...f, kind: e.target.value as 'material' | 'tool' })}
          >
            {(Object.keys(ITEM_KINDS) as Array<keyof typeof ITEM_KINDS>).map((k) => (
              <option key={k} value={k}>
                {ITEM_KINDS[k]}
              </option>
            ))}
          </Select>
        </Field>
      </div>
      {dups.length ? (
        <div className="rounded-md bg-warning-soft px-4 py-3 text-[13px]">
          <p className="font-medium">
            Похожие позиции уже есть — портал их не объединяет, решение за вами (СК-02):
          </p>
          <ul className="mt-1 flex flex-col gap-0.5">
            {dups.map((s) => (
              <li key={s.item.id} className="flex flex-wrap items-center gap-2">
                <Badge tone={s.score >= 90 ? 'danger' : s.score >= 70 ? 'warning' : 'neutral'}>
                  {s.score} %
                </Badge>
                <Link to={`/warehouse/items/${s.item.id}`} className="hover:underline">
                  {s.item.code} · {s.item.name}
                </Link>
                <span className="text-ink-2">{s.why}</span>
              </li>
            ))}
          </ul>
        </div>
      ) : null}
      <div className="grid gap-4 sm:grid-cols-3">
        <AgskClassField
          value={f.agskClass}
          itemKind={f.kind}
          onChange={(agskClass) => setF((prev) => ({ ...prev, agskClass }))}
          onPickUnit={(unit) =>
            setF((prev) => (!prev.unit.trim() || prev.unit === 'шт' ? { ...prev, unit } : prev))
          }
        />
        <Field label="Единица учёта">
          <Input
            value={f.unit}
            onChange={(e) => setF({ ...f, unit: e.target.value })}
            placeholder="м, т, шт"
          />
        </Field>
        <Field label="Пересчёт" hint="1 единица учёта = коэффициент × вторая единица">
          <div className="grid grid-cols-[1fr_1fr] gap-2">
            <Input
              inputMode="decimal"
              placeholder="0,0163"
              value={f.convFactor}
              onChange={(e) => setF({ ...f, convFactor: e.target.value })}
            />
            <Input
              placeholder="т"
              value={f.convUnit}
              onChange={(e) => setF({ ...f, convUnit: e.target.value })}
            />
          </div>
        </Field>
      </div>
      <fieldset>
        <legend className="mb-1.5 text-[13px] font-medium">Свойства</legend>
        <div className="grid gap-3 sm:grid-cols-4">
          {(Object.keys(ITEM_PROPS) as ItemProp[]).map((k) => (
            <Field key={k} label={ITEM_PROPS[k]}>
              <Input
                value={f.props[k] ?? ''}
                onChange={(e) => setF({ ...f, props: { ...f.props, [k]: e.target.value } })}
              />
            </Field>
          ))}
        </div>
      </fieldset>
      <div className="grid gap-4 sm:grid-cols-3">
        <Field label="Срок годности, дней">
          <Input
            inputMode="numeric"
            value={f.shelfLifeDays}
            onChange={(e) => setF({ ...f, shelfLifeDays: e.target.value })}
          />
        </Field>
        <Field label="Неснижаемый запас" hint="расходники: ниже — потребность на пополнение (СК-18)">
          <Input
            inputMode="decimal"
            value={f.minStock}
            onChange={(e) => setF({ ...f, minStock: e.target.value })}
          />
        </Field>
        <Field label="Целевой запас">
          <Input
            inputMode="decimal"
            value={f.targetStock}
            onChange={(e) => setF({ ...f, targetStock: e.target.value })}
          />
        </Field>
      </div>
      <Field label="Код номенклатуры в 1С" hint="для выгрузки документов в 1С (СК-35)">
        <Input value={f.code1c} onChange={(e) => setF({ ...f, code1c: e.target.value })} />
        {(hints.data ?? []).length ? (
          <div className="mt-2 flex flex-wrap gap-2">
            {(hints.data ?? []).slice(0, 5).map((h) => (
              <button
                key={h.code}
                type="button"
                onClick={() => setF({ ...f, code1c: h.code })}
                className="inline-flex min-h-11 items-center rounded-md border border-line bg-surface px-3 text-left text-[13px] hover:border-line-strong"
                title={h.name}
              >
                <span className="num font-medium">{h.code}</span>
                <span className="ml-2 max-w-[220px] truncate text-ink-2">{h.name}</span>
              </button>
            ))}
          </div>
        ) : null}
      </Field>
      <div className="flex flex-wrap gap-x-6 gap-y-2">
        <Checkbox
          label="Нужен сертификат"
          description="поступление ждёт входного контроля"
          checked={f.certRequired}
          onChange={(e) => setF({ ...f, certRequired: e.target.checked })}
        />
        <Checkbox
          label="Нужен паспорт качества"
          checked={f.passportRequired}
          onChange={(e) => setF({ ...f, passportRequired: e.target.checked })}
        />
        {initial ? (
          <Checkbox
            label="Позиция действует"
            checked={f.isActive}
            onChange={(e) => setF({ ...f, isActive: e.target.checked })}
          />
        ) : null}
      </div>
      <Field label="Примечание">
        <Textarea rows={2} value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} />
      </Field>
      <ErrorBox error={m.error} />
      <div className="flex gap-2">
        <Button
          variant="primary"
          loading={m.isPending}
          disabled={f.name.trim().length < 2 || !f.unit.trim()}
          onClick={() => m.mutate()}
        >
          {initial ? 'Сохранить позицию' : 'Завести позицию'}
        </Button>
        {onCancel ? (
          <Button variant="ghost" onClick={onCancel}>
            Отмена
          </Button>
        ) : null}
      </div>
    </div>
  );
}

/* ───────────────────────── Документ ───────────────────────── */

type LineDraft = {
  k: string;
  itemId: string;
  quantity: string;
  price: string;
  batch: string;
  certNumber: string;
  passportNumber: string;
  expiresOn: string;
  reservationId: string;
  normQuantity: string;
  note: string;
};

const emptyLine = (): LineDraft => ({
  k: formKey(),
  itemId: '',
  quantity: '',
  price: '',
  batch: '',
  certNumber: '',
  passportNumber: '',
  expiresOn: '',
  reservationId: '',
  normQuantity: '',
  note: '',
});

/** Новый документ или правка черновика: шапка по виду документа и строки */
export function DocumentDialog({
  open,
  onOpenChange,
  edit,
  presetKind,
  presetWarehouseId,
  onSaved,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  edit?: DocumentDetail;
  presetKind?: DocKind;
  presetWarehouseId?: string;
  onSaved: (d: DocumentDetail) => void;
}) {
  const me = useMe();
  const money = seesMoney(me.access, 'warehouse');
  const qc = useQueryClient();
  const warehouses = useQuery(warehousesQuery);
  const catalog = useQuery(itemsQuery());
  const people = useQuery(peopleQuery);
  const suppliers = useQuery(counterpartiesQuery('supplier'));
  const [key] = useState(formKey);
  // Для заявленного расхода позиции — то, что подтверждённо числится за самим сотрудником (СК-23)
  const own = useQuery({
    ...holdingsQuery({ userId: me.user.id }),
    enabled: (edit?.kind ?? presetKind) === 'consume',
  });
  const [f, setF] = useState({
    kind: (edit?.kind ?? presetKind ?? 'receipt') as DocKind,
    docDate: edit?.docDate ?? todayLocal(),
    warehouseId: edit?.warehouseId ?? presetWarehouseId ?? '',
    toWarehouseId: edit?.toWarehouseId ?? '',
    counterpartyId: edit?.counterpartyId ?? '',
    contractId: edit?.contractId ?? '',
    recipientUserId: edit?.recipientUserId ?? '',
    activityId: edit?.activityId ?? '',
    limitReason: edit?.limit?.reason ?? '',
    basis: edit?.basis ?? '',
    reason: edit?.reason ?? '',
    note: edit?.note ?? '',
  });
  // Договоры поставщика (подписанные, без сумм): поступление по договору закрывает поставку по его графику
  const contractsQ = useQuery({
    queryKey: ['wh', 'contracts', f.counterpartyId],
    queryFn: () =>
      api<Array<{ id: string; number: string; subject: string }>>('/warehouse/contracts', {
        query: { counterpartyId: f.counterpartyId },
      }),
    enabled: f.kind === 'receipt' && Boolean(f.counterpartyId),
  });
  // Выдача на работу графика: лимит = объём × норма; сверх лимита нужна причина и согласие РП (СК-20)
  const works = useQuery({
    queryKey: ['wh', 'work-limits', f.warehouseId],
    queryFn: () => api<WorkLimit[]>('/warehouse/work-limits', { query: { warehouseId: f.warehouseId } }),
    enabled: f.kind === 'issue' && Boolean(f.warehouseId),
  });
  const [lines, setLines] = useState<LineDraft[]>(() =>
    edit
      ? edit.lineList.map((l) => ({
          k: l.id,
          itemId: l.itemId,
          quantity: String(l.quantity),
          price: l.price === null ? '' : String(l.price),
          batch: l.batch ?? '',
          certNumber: l.certNumber ?? '',
          passportNumber: l.passportNumber ?? '',
          expiresOn: l.expiresOn ?? '',
          reservationId: l.reservationId ?? '',
          normQuantity: l.normQuantity === null ? '' : String(l.normQuantity),
          note: l.note ?? '',
        }))
      : [emptyLine()],
  );
  const dir = DOC_KINDS[f.kind].direction;
  const items =
    f.kind === 'consume'
      ? {
          data: (own.data ?? [])
            .filter((h) => h.quantity > 0)
            .map((h) => ({
              id: h.itemId,
              code: h.itemCode,
              name: h.itemName,
              unit: h.unit,
              quantity: h.quantity,
            })),
        }
      : {
          data: catalog.data?.map((x) => ({
            id: x.id,
            code: x.code,
            name: x.name,
            unit: x.unit,
            quantity: null as number | null,
          })),
        };
  const [consumeReason, setConsumeReason] = useState<ConsumeReason>('consumed');
  // Расход и потери заявляет сам получатель по любому своему складу; остальные документы — кладовщик своего склада
  const operable = (warehouses.data ?? []).filter(
    (w) =>
      (f.kind === 'consume' || w.canOperate) &&
      w.kind !== 'transit' &&
      w.isActive &&
      // Отчёт подрядчика о расходе — только по складу «у подрядчика»
      (f.kind !== 'contractor_report' || w.kind === 'contractor') &&
      // Материалы заказчика подрядчику не передаются (СК-25)
      (w.kind !== 'customer' || f.kind !== 'contractor'),
  );
  const stock = useQuery({
    ...stockQuery({ warehouseId: f.warehouseId }),
    enabled: Boolean(f.warehouseId) && dir !== 'in',
  });
  const availableOf = (itemId: string) => {
    if (f.kind === 'consume') {
      const h = items.data?.find((x) => x.id === itemId);
      return h?.quantity !== null && h?.quantity !== undefined ? { available: h.quantity } : undefined;
    }
    return stock.data?.find((s) => s.itemId === itemId);
  };
  const m = useMutation({
    mutationFn: () => {
      const body: DocumentInput & { reason?: string | null } = {
        kind: f.kind,
        docDate: f.docDate,
        warehouseId: f.warehouseId,
        toWarehouseId: dir === 'move' ? f.toWarehouseId || null : null,
        counterpartyId: f.counterpartyId || null,
        // Договор — только у поступления; у других видов связь, поставленная через API, не трогаем
        ...(f.kind === 'receipt' ? { contractId: f.counterpartyId ? f.contractId || null : null } : {}),
        recipientUserId:
          f.kind === 'consume'
            ? me.user.id
            : dir === 'to_holder' || dir === 'from_holder'
              ? f.recipientUserId || null
              : null,
        activityId: f.kind === 'issue' ? f.activityId || null : null,
        limitReason: f.kind === 'issue' && f.activityId ? f.limitReason.trim() || null : null,
        basis: f.basis.trim() || null,
        reason:
          f.kind === 'consume'
            ? `${CONSUME_REASONS[consumeReason]}: ${f.reason.trim()}`
            : f.reason.trim() || null,
        note: f.note.trim() || null,
        clientKey: edit ? undefined : key,
        lines: lines
          .filter((l) => l.itemId && l.quantity.trim())
          .map((l) => ({
            itemId: l.itemId,
            quantity: parseNum(l.quantity),
            price: money && dir === 'in' && l.price.trim() ? parseNum(l.price) : null,
            batch: l.batch.trim() || null,
            certNumber: l.certNumber.trim() || null,
            passportNumber: l.passportNumber.trim() || null,
            expiresOn: l.expiresOn || null,
            reservationId: l.reservationId || null,
            normQuantity:
              f.kind === 'contractor_report' && l.normQuantity.trim() ? parseNum(l.normQuantity) : null,
            note: l.note.trim() || null,
          })),
      };
      if (edit) {
        const { kind: _k, clientKey: _c, ...rest } = body;
        return api<DocumentDetail>(`/warehouse/documents/${edit.id}`, { method: 'PUT', body: rest });
      }
      return api<DocumentDetail>('/warehouse/documents', { body });
    },
    onSuccess: (d) => {
      qc.invalidateQueries({ queryKey: ['wh'] });
      toast(edit ? 'Черновик сохранён' : `Документ ${d.number} создан — проверьте и проведите`);
      onOpenChange(false);
      onSaved(d);
    },
  });
  const valid =
    f.warehouseId &&
    lines.some((l) => l.itemId && parseNum(l.quantity) > 0) &&
    (dir !== 'move' || f.toWarehouseId) &&
    ((dir !== 'to_holder' && dir !== 'from_holder') || f.recipientUserId) &&
    ((f.kind !== 'writeoff' && f.kind !== 'consume') || f.reason.trim().length >= 3) &&
    (f.kind !== 'contractor_report' ||
      lines.every((l) => !l.itemId || (parseNum(l.normQuantity) >= 0 && l.normQuantity.trim())));
  const update = (k: string, patch: Partial<LineDraft>) =>
    setLines((ls) => ls.map((l) => (l.k === k ? { ...l, ...patch } : l)));
  const fromWh = (warehouses.data ?? []).find((w) => w.id === f.warehouseId);
  const customers = useQuery({ ...counterpartiesQuery('customer'), enabled: fromWh?.kind === 'customer' });
  const customerName = (customers.data ?? []).find((c) => c.id === fromWh?.counterpartyId)?.name;
  const toWarehouses = (warehouses.data ?? []).filter(
    (w) =>
      w.id !== f.warehouseId &&
      w.kind !== 'transit' &&
      w.isActive &&
      (f.kind === 'contractor' ? w.kind === 'contractor' : w.kind !== 'contractor') &&
      // Материалы заказчика не смешиваются со своими: перемещение — только между складами того же заказчика (СК-25)
      (fromWh?.kind === 'customer'
        ? w.kind === 'customer' && w.counterpartyId === fromWh.counterpartyId
        : w.kind !== 'customer'),
  );
  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title={edit ? `Черновик ${edit.number}` : 'Новый документ склада'}
      description="Документ создаётся черновиком; остаток меняет только проведение"
      width="lg"
      footer={
        <>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Отмена
          </Button>
          <Button variant="primary" loading={m.isPending} disabled={!valid} onClick={() => m.mutate()}>
            {edit
              ? 'Сохранить черновик'
              : f.kind === 'consume' || f.kind === 'contractor_report'
                ? 'Отправить руководителю проекта'
                : 'Создать черновик'}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <div className="grid gap-4 sm:grid-cols-3">
          <Field label="Вид документа">
            <Select
              value={f.kind}
              disabled={Boolean(edit)}
              onChange={(e) => {
                const kind = e.target.value as DocKind;
                // Склад, который не подходит новому виду (например, обычный для отчёта подрядчика), сбрасывается
                const keep = (warehouses.data ?? []).some(
                  (w) => w.id === f.warehouseId && (kind !== 'contractor_report' || w.kind === 'contractor'),
                );
                setF({ ...f, kind, toWarehouseId: '', warehouseId: keep ? f.warehouseId : '' });
              }}
            >
              {CREATABLE_DOC_KINDS.map((k) => (
                <option key={k} value={k}>
                  {DOC_KINDS[k].title}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Дата">
            <Input type="date" value={f.docDate} onChange={(e) => setF({ ...f, docDate: e.target.value })} />
          </Field>
          <Field label={dir === 'move' ? 'Склад-отправитель' : 'Склад'}>
            <Select value={f.warehouseId} onChange={(e) => setF({ ...f, warehouseId: e.target.value })}>
              <option value="">Выберите склад</option>
              {operable.map((w) => (
                <option key={w.id} value={w.id}>
                  {w.name}
                  {w.projectCode ? ` · ${w.projectCode}` : ''}
                </option>
              ))}
            </Select>
          </Field>
          {dir === 'move' ? (
            <Field
              label="Склад-получатель"
              hint={
                f.kind === 'transfer'
                  ? 'Перемещение идёт через склад «В пути», получатель подтверждает принятое'
                  : undefined
              }
            >
              <Select value={f.toWarehouseId} onChange={(e) => setF({ ...f, toWarehouseId: e.target.value })}>
                <option value="">Выберите склад</option>
                {toWarehouses.map((w) => (
                  <option key={w.id} value={w.id}>
                    {w.name}
                    {w.projectCode ? ` · ${w.projectCode}` : ''}
                  </option>
                ))}
              </Select>
            </Field>
          ) : null}
          {dir === 'to_holder' || dir === 'from_holder' ? (
            <Field label={dir === 'to_holder' ? 'Получатель' : 'Кто возвращает'}>
              <Select
                value={f.recipientUserId}
                onChange={(e) => setF({ ...f, recipientUserId: e.target.value })}
              >
                <option value="">Выберите сотрудника</option>
                {(people.data ?? []).map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.fullName}
                  </option>
                ))}
              </Select>
            </Field>
          ) : null}
          {f.kind === 'issue' ? (
            <Field
              label="Работа графика"
              hint="лимит выдачи — объём работы × норма расхода из сметы; сверх лимита нужна причина и согласие руководителя проекта"
            >
              <Select value={f.activityId} onChange={(e) => setF({ ...f, activityId: e.target.value })}>
                <option value="">Без привязки к работе</option>
                {(works.data ?? []).map((w) => (
                  <option key={w.activityId} value={w.activityId}>
                    {w.code} — {w.name}
                  </option>
                ))}
              </Select>
            </Field>
          ) : null}
          {f.kind === 'issue' && f.activityId ? (
            <LimitHint
              work={(works.data ?? []).find((w) => w.activityId === f.activityId)}
              asked={lines.map((l) => ({ itemId: l.itemId, quantity: parseNum(l.quantity) }))}
              reason={f.limitReason}
              onReason={(v) => setF({ ...f, limitReason: v })}
            />
          ) : null}
          {f.kind === 'receipt' && fromWh?.kind === 'customer' ? (
            <Field label="От кого" hint="на склад материалов заказчика приходуется только от этого заказчика">
              <Input value={customerName ?? 'Заказчик склада'} readOnly />
            </Field>
          ) : f.kind === 'receipt' ? (
            <Field label="Поставщик">
              <Select
                value={f.counterpartyId}
                onChange={(e) => setF({ ...f, counterpartyId: e.target.value, contractId: '' })}
              >
                <option value="">—</option>
                {(suppliers.data ?? []).map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </Select>
            </Field>
          ) : null}
          {f.kind === 'receipt' && f.counterpartyId && (contractsQ.data ?? []).length ? (
            <Field label="Договор" hint="поступление закрывает поставку по графику договора">
              <Select value={f.contractId} onChange={(e) => setF({ ...f, contractId: e.target.value })}>
                <option value="">Без договора</option>
                {(contractsQ.data ?? []).map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.number} — {c.subject}
                  </option>
                ))}
              </Select>
            </Field>
          ) : null}
          <Field
            label="Основание"
            hint="накладная, СНТ, ЭСФ, требование прораба"
            className={dir === 'in' && f.kind !== 'receipt' ? 'sm:col-span-2' : undefined}
          >
            <Input value={f.basis} onChange={(e) => setF({ ...f, basis: e.target.value })} />
          </Field>
          {f.kind === 'writeoff' ? (
            <Field label="Причина списания" className="sm:col-span-3">
              <Input value={f.reason} onChange={(e) => setF({ ...f, reason: e.target.value })} />
            </Field>
          ) : null}
          {f.kind === 'consume' ? (
            <>
              <Field
                label="Что случилось"
                hint="заявляет получатель по материалам на своих руках; до решения руководителя проекта ничего не меняется (СК-23)"
              >
                <Select
                  value={consumeReason}
                  onChange={(e) => setConsumeReason(e.target.value as ConsumeReason)}
                >
                  {(Object.keys(CONSUME_REASONS) as ConsumeReason[]).map((k) => (
                    <option key={k} value={k}>
                      {CONSUME_REASONS[k]}
                    </option>
                  ))}
                </Select>
              </Field>
              <Field label="Пояснение" className="sm:col-span-2">
                <Input
                  value={f.reason}
                  onChange={(e) => setF({ ...f, reason: e.target.value })}
                  placeholder="какая работа, что произошло"
                />
              </Field>
            </>
          ) : null}
        </div>
        <div className="flex flex-col gap-3">
          <div className="flex items-center justify-between">
            <span className="text-[13px] font-medium">Позиции</span>
            <Button
              size="sm"
              icon={<Plus className="size-4" />}
              onClick={() => setLines((ls) => [...ls, emptyLine()])}
            >
              Строка
            </Button>
          </div>
          {lines.map((l, i) => {
            const it = items.data?.find((x) => x.id === l.itemId);
            const av = l.itemId ? availableOf(l.itemId) : undefined;
            return (
              <div key={l.k} className="rounded-md border border-line p-3">
                <div className="grid gap-3 sm:grid-cols-[minmax(0,3fr)_minmax(0,1fr)_minmax(0,1fr)_auto]">
                  <Field label={`Позиция ${i + 1}`}>
                    <Select value={l.itemId} onChange={(e) => update(l.k, { itemId: e.target.value })}>
                      <option value="">Выберите из каталога</option>
                      {(items.data ?? []).map((x) => (
                        <option key={x.id} value={x.id}>
                          {x.code} · {x.name} ({x.unit})
                        </option>
                      ))}
                    </Select>
                  </Field>
                  <Field
                    label={`Количество${it ? `, ${it.unit}` : ''}`}
                    error={
                      av && dir !== 'in' && parseNum(l.quantity) > av.available
                        ? `Доступно ${qty(av.available)}`
                        : undefined
                    }
                    hint={av && dir !== 'in' ? `доступно ${qty(av.available)}` : undefined}
                  >
                    <Input
                      inputMode="decimal"
                      value={l.quantity}
                      onChange={(e) => update(l.k, { quantity: e.target.value })}
                    />
                  </Field>
                  {f.kind === 'contractor_report' ? (
                    <Field
                      label="Норма расхода"
                      hint={
                        l.normQuantity.trim() && parseNum(l.quantity) > parseNum(l.normQuantity)
                          ? `перерасход ${qty(parseNum(l.quantity) - parseNum(l.normQuantity))} — на решение РП`
                          : 'обязательна: сверх нормы — перерасход на решение РП'
                      }
                    >
                      <Input
                        inputMode="decimal"
                        value={l.normQuantity}
                        onChange={(e) => update(l.k, { normQuantity: e.target.value })}
                      />
                    </Field>
                  ) : null}
                  {dir === 'in' ? (
                    <Field label="Цена без НДС" hint={money ? undefined : 'нужен уровень денег «Склад»'}>
                      <Input
                        inputMode="decimal"
                        disabled={!money}
                        value={l.price}
                        onChange={(e) => update(l.k, { price: e.target.value })}
                      />
                    </Field>
                  ) : f.kind === 'contractor_report' ? null : (
                    <div className="hidden sm:block" />
                  )}
                  <div className="flex items-end">
                    <IconButton
                      label="Убрать строку"
                      onClick={() => setLines((ls) => ls.filter((x) => x.k !== l.k))}
                    >
                      <Trash2 className="size-4" />
                    </IconButton>
                  </div>
                </div>
                {f.kind === 'receipt' ? (
                  <div className="mt-3 grid gap-3 sm:grid-cols-4">
                    <Field label="Партия">
                      <Input value={l.batch} onChange={(e) => update(l.k, { batch: e.target.value })} />
                    </Field>
                    <Field label="Сертификат №">
                      <Input
                        value={l.certNumber}
                        onChange={(e) => update(l.k, { certNumber: e.target.value })}
                      />
                    </Field>
                    <Field label="Паспорт №">
                      <Input
                        value={l.passportNumber}
                        onChange={(e) => update(l.k, { passportNumber: e.target.value })}
                      />
                    </Field>
                    <Field label="Годен до">
                      <Input
                        type="date"
                        value={l.expiresOn}
                        onChange={(e) => update(l.k, { expiresOn: e.target.value })}
                      />
                    </Field>
                  </div>
                ) : null}
              </div>
            );
          })}
        </div>
        <Field label="Примечание">
          <Textarea rows={2} value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} />
        </Field>
        <ErrorBox error={m.error} />
      </div>
    </Dialog>
  );
}

/* ───────────────────────── Резерв ───────────────────────── */

export function ReservationDialog({
  open,
  onOpenChange,
  presetWarehouseId,
  presetItemId,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  presetWarehouseId?: string;
  presetItemId?: string;
}) {
  const qc = useQueryClient();
  const warehouses = useQuery(warehousesQuery);
  const items = useQuery(itemsQuery());
  const [f, setF] = useState({
    warehouseId: presetWarehouseId ?? '',
    itemId: presetItemId ?? '',
    quantity: '',
    workRef: '',
    reason: '',
  });
  const stock = useQuery({
    ...stockQuery({ warehouseId: f.warehouseId, itemId: f.itemId }),
    enabled: Boolean(f.warehouseId && f.itemId),
  });
  const av = stock.data?.[0];
  const m = useMutation({
    mutationFn: () => {
      const body: ReservationInput = {
        warehouseId: f.warehouseId,
        itemId: f.itemId,
        quantity: parseNum(f.quantity),
        workRef: f.workRef.trim() || null,
        reason: f.reason.trim(),
      };
      return api<{ id: string }>('/warehouse/reservations', { body });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['wh'] });
      toast('Резерв поставлен');
      onOpenChange(false);
    },
  });
  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title="Резерв под работу"
      description="Ручной резерв — с причиной; резерв из графика появится вместе с разделом «График» (СК-11)"
      footer={
        <>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Отмена
          </Button>
          <Button
            variant="primary"
            loading={m.isPending}
            disabled={
              !f.warehouseId || !f.itemId || !(parseNum(f.quantity) > 0) || f.reason.trim().length < 3
            }
            onClick={() => m.mutate()}
          >
            Поставить резерв
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <Field label="Склад">
          <Select value={f.warehouseId} onChange={(e) => setF({ ...f, warehouseId: e.target.value })}>
            <option value="">Выберите склад</option>
            {(warehouses.data ?? [])
              .filter((w) => w.canOperate && w.kind !== 'transit')
              .map((w) => (
                <option key={w.id} value={w.id}>
                  {w.name}
                </option>
              ))}
          </Select>
        </Field>
        <Field label="Позиция">
          <Select value={f.itemId} onChange={(e) => setF({ ...f, itemId: e.target.value })}>
            <option value="">Выберите из каталога</option>
            {(items.data ?? []).map((x) => (
              <option key={x.id} value={x.id}>
                {x.code} · {x.name} ({x.unit})
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Количество" hint={av ? `доступно ${qty(av.available)} ${av.unit}` : undefined}>
          <Input
            inputMode="decimal"
            value={f.quantity}
            onChange={(e) => setF({ ...f, quantity: e.target.value })}
          />
        </Field>
        <Field label="Работа графика" hint="номер или название работы">
          <Input value={f.workRef} onChange={(e) => setF({ ...f, workRef: e.target.value })} />
        </Field>
        <Field label="Причина">
          <Textarea rows={2} value={f.reason} onChange={(e) => setF({ ...f, reason: e.target.value })} />
        </Field>
        <ErrorBox error={m.error} />
      </div>
    </Dialog>
  );
}

/** Остаток лимита по работе и, если выдача его превышает, поле причины (СК-20) */
function LimitHint({
  work,
  asked,
  reason,
  onReason,
}: {
  work: WorkLimit | undefined;
  asked: Array<{ itemId: string; quantity: number }>;
  reason: string;
  onReason: (v: string) => void;
}) {
  if (!work) return null;
  const over = work.rows.filter((r) => {
    const q = asked
      .filter((a) => r.itemId && a.itemId === r.itemId)
      .reduce((n, a) => n + (a.quantity || 0), 0);
    return q > r.left + 1e-9;
  });
  return (
    <div className="space-y-2 rounded-md border border-line p-3 text-[13px] md:col-span-2">
      <div className="font-medium text-ink">Лимит по работе «{work.name}»</div>
      <ul className="space-y-1 text-ink-2">
        {work.rows.map((r) => (
          <li key={`${r.name}|${r.unit}`}>
            {r.name}: лимит {r.limit} {r.unit}, выдано {r.issued}, осталось {r.left}
          </li>
        ))}
      </ul>
      {over.length ? (
        <Field
          label="Причина выдачи сверх лимита"
          hint={`превышен лимит: ${over.map((r) => r.name).join(', ')}. Выдачу проведёт кладовщик после согласия руководителя проекта`}
        >
          <Textarea rows={2} value={reason} onChange={(e) => onReason(e.target.value)} />
        </Field>
      ) : null}
    </div>
  );
}
